/**
 * Catalog-driven view models for the PricingSection component.
 *
 * Keeps PricingSection.tsx free of price math and null-handling; gives us
 * a single place to test rounding, currency, and yearly-discount logic.
 */

import type {
  BillingInterval,
  CatalogBundle,
  CatalogData,
  CatalogProduct,
  ProductTier,
} from "@d-m-organization/pricing-ui";
import { pickPrimaryBundle } from "./derivePlanView.js";

// ───────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────

export type PriceCard = {
  /** Display name from the catalog. */
  readonly displayName: string;
  /** Monthly euro price (rounded to whole euro for the big number). */
  readonly priceEur: number;
  /** Decimal price for fine-grained display, if needed elsewhere. */
  readonly priceCents: number;
  /** True if the user picked yearly billing. */
  readonly isYearly: boolean;
};

export type PricingViewModel = {
  /** Per-product price cards (à-la-carte view). */
  readonly products: readonly (PriceCard & { id: string; tierName: string })[];
  /** Primary upsell bundle (Pro). null if catalog has none. */
  readonly bundle: PriceCard | null;
  /**
   * Sum of all visible products' Pro monthly prices, in whole euros.
   * Used for "saves €X vs. singles" copy. null if catalog empty.
   */
  readonly singlesTotalEur: number | null;
  /**
   * Bundle savings vs. summing only the products the bundle includes,
   * in whole euros. null when no bundle or no savings.
   */
  readonly bundleSavingsEur: number | null;
  /**
   * Average yearly discount as a whole percent (e.g. 20 for "−20%").
   * Computed from products. 0 when no yearly tiers exist.
   */
  readonly yearlyDiscountPct: number;
};

// ───────────────────────────────────────────────────────────────────────────
// Public API
// ───────────────────────────────────────────────────────────────────────────

/**
 * Build the full pricing view model for the given catalog + user choices.
 * Returns null when catalog is null (loading state).
 */
export function buildPricingViewModel(
  catalog: CatalogData | null,
  interval: BillingInterval,
  tierForProductView: ProductTier = "pro",
): PricingViewModel | null {
  if (!catalog) return null;

  const visible = catalog.products.filter((p) => p.visible);
  const products = visible.map((p) => productPriceCard(p, interval, tierForProductView));

  const primaryBundle = pickPrimaryBundle(catalog.bundles);
  const bundle = primaryBundle ? bundlePriceCard(primaryBundle, interval) : null;

  // singlesTotal: sum of all visible products' Pro monthly prices
  const singlesTotalCents = visible.reduce((sum, p) => {
    const tier = p.tiers["pro"];
    if (!tier?.active) return sum;
    return sum + tier.monthlyPriceCents;
  }, 0);
  const singlesTotalEur = visible.length > 0 ? Math.round(singlesTotalCents / 100) : null;

  // bundleSavings: only count products the bundle actually includes (HD-98 R3 lesson)
  let bundleSavingsEur: number | null = null;
  if (primaryBundle) {
    const includedSet = new Set(primaryBundle.includedProducts);
    const includedSinglesCents = visible
      .filter((p) => includedSet.has(p.productId))
      .reduce((sum, p) => sum + (p.tiers["pro"]?.active ? p.tiers["pro"].monthlyPriceCents : 0), 0);
    const savingsCents = includedSinglesCents - primaryBundle.monthlyPriceCents;
    bundleSavingsEur = savingsCents > 0 ? Math.round(savingsCents / 100) : null;
  }

  return {
    products,
    bundle,
    singlesTotalEur,
    bundleSavingsEur,
    yearlyDiscountPct: averageYearlyDiscountPct(visible),
  };
}

// ───────────────────────────────────────────────────────────────────────────
// Helpers (exported for tests)
// ───────────────────────────────────────────────────────────────────────────

export function productPriceCard(
  product: CatalogProduct,
  interval: BillingInterval,
  tier: ProductTier = "pro",
): PriceCard & { id: string; tierName: string } {
  const tierInfo = product.tiers[tier];
  const monthlyEquivCents = tierInfo
    ? interval === "year"
      ? Math.round(tierInfo.annualPriceCents / 12)
      : tierInfo.monthlyPriceCents
    : 0;
  return {
    id: product.productId,
    tierName: tier,
    displayName: product.displayName,
    priceCents: monthlyEquivCents,
    priceEur: Math.round(monthlyEquivCents / 100),
    isYearly: interval === "year",
  };
}

export function bundlePriceCard(
  bundle: CatalogBundle,
  interval: BillingInterval,
): PriceCard {
  const monthlyEquivCents =
    interval === "year"
      ? Math.round(bundle.annualPriceCents / 12)
      : bundle.monthlyPriceCents;
  return {
    displayName: bundle.displayName,
    priceCents: monthlyEquivCents,
    priceEur: Math.round(monthlyEquivCents / 100),
    isYearly: interval === "year",
  };
}

/**
 * Average yearly discount across visible products' paid tiers, rounded
 * to the nearest 5%. Returns 0 if no products have yearly pricing.
 *
 * Mirrors pricing-ui's `computeAnnualSavingsPercent` but operates on the
 * full visible-product set, so the dashboard pill matches what users
 * would compute manually.
 */
export function averageYearlyDiscountPct(
  products: readonly CatalogProduct[],
): number {
  let totalDiscount = 0;
  let count = 0;

  for (const product of products) {
    for (const [tierKey, info] of Object.entries(product.tiers)) {
      if (tierKey === "free") continue;
      if (!info.active || info.monthlyPriceCents === 0) continue;
      const yearlyEquiv = info.monthlyPriceCents * 12;
      if (yearlyEquiv === 0) continue;
      const discount = 1 - info.annualPriceCents / yearlyEquiv;
      if (discount <= 0) continue;
      totalDiscount += discount;
      count++;
    }
  }

  if (count === 0) return 0;
  const avg = totalDiscount / count;
  return Math.round((avg * 100) / 5) * 5;
}
